import { toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, SerializedData, DataModelBuilder, DataModel } from '../dto/data-model.js';
import { isSameList, isSameData } from '../dto/data-model-utils.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { PaymentType } from './payment-type.js';

/**
 * @deprecated Use {@link PaymentProtocol} instead.
 */
export class LegacyPaymentSettings extends DataModel {
  static readonly TYPE_ID: string = '83ca8be665134c7e8bdebdfe91864614';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly paymentAccount: PaymentAccountRef | null,
    public readonly paymentMethods: PaymentType[],
    public readonly allowLegalEntityInvoices: boolean,
    public readonly allowOfflineOrderReceipts: boolean,
  ) {
    super();
  }

  get $typeId() {
    return LegacyPaymentSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacyPaymentSettings.SCHEMA_VERSION);
  }

  static create(args: {
    paymentAccount: PaymentAccountRef | null,
    paymentMethods: PaymentType[],
    allowLegalEntityInvoices: boolean,
    allowOfflineOrderReceipts: boolean,
  }): LegacyPaymentSettings {
    return new LegacyPaymentSettings(
        args.paymentAccount,
        args.paymentMethods,
        args.allowLegalEntityInvoices,
        args.allowOfflineOrderReceipts,
    );
  }

  static empty(): LegacyPaymentSettings {
    return this.create({
      paymentAccount: null,
      paymentMethods: [],
      allowLegalEntityInvoices: false,
      allowOfflineOrderReceipts: false,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyPaymentSettings {
    return new LegacyPaymentSettings(
        context.fromDataOrNull<PaymentAccountRef>(MapUtils.getRecordOrNull(map, 'paymentAccount')),
        MapUtils.getList<PaymentType>(
            map,
            'paymentMethods',
            (value) => toEnumFromString(value as string, PaymentType) ?? PaymentType.OnDelivery
        ),
        MapUtils.getBoolean(map, 'allowLegalEntityInvoices'),
        MapUtils.getBoolean(map, 'allowOfflineOrderReceipts'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'paymentAccount': context.toDataOrNull(this.paymentAccount),
      'paymentMethods': this.paymentMethods,
      'allowLegalEntityInvoices': this.allowLegalEntityInvoices,
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
    };
  }

  clone(): this {
    return new LegacyPaymentSettings(
        this.paymentAccount?.clone() ?? null,
        this.paymentMethods.map((e) => e),
        this.allowLegalEntityInvoices,
        this.allowOfflineOrderReceipts,
    ) as this;
  }

  isSame(other: LegacyPaymentSettings): boolean {
    if(this === other) {
      return true;
    }

    return isSameList(other.paymentMethods, this.paymentMethods) &&
        isSameData(other.paymentAccount, this.paymentAccount) &&
        other.allowLegalEntityInvoices === this.allowLegalEntityInvoices;
  }
}

export class LegacyPaymentSettingsBuilder extends DataModelBuilder<LegacyPaymentSettings> {
  build(context: SerializationContext, map: SerializedData): LegacyPaymentSettings {
    return LegacyPaymentSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyPaymentSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyPaymentSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [allowLegalEntityInvoices] was added
    if(version < 2) {
      map['allowLegalEntityInvoices'] = false;
      version = 2;
    }

    // Version 3:
    // - add `allowOfflineOrderReceipts` of type [boolean].
    // - note: before 2026.01.26. the default value was `true`
    if(version < 3) {
      map['allowOfflineOrderReceipts'] ??= false;
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
